/**
 * Gift System Manager
 * Sistema de Brindes para Shopify Theme
 *
 * @version 1.0.0
 * @author LFpro Team
 */


class GiftSystemManager extends HTMLElement {
  constructor() {
    super();


    // Carregar configurações do settings
    this.config = window.giftSystemConfig || { enabled: false };
    this.levels = this.config.levels || [];


    // Validar configuração
    if (!this.config.enabled || this.levels.length === 0) {
      this.config.enabled = false;
    }

    // Estado atual
    this.currentCart = null;
    this.previousCart = null; // Para detectar remoções manuais
    this.unlockedLevels = [];
    this.addedGifts = [];
    this.isInitializing = false;
    this.isProcessing = false;
    this.cartUpdateTimeout = null;
    
    // Cache de produtos de brinde (otimização)
    this.productCache = new Map();

    // Elementos DOM (serão definidos no connectedCallback)
    this.progressBar = null;
    this.progressFill = null;
    this.progressMessage = null;
  }

  connectedCallback() {

    // Verificar se o sistema está habilitado
    if (!this.config.enabled) {
      return;
    }

    // Aguardar tema estar completamente carregado (window.onload)
    // Isso garante que o gift-system não interfere com inicialização do tema
    if (document.readyState === 'complete') {
      // Já carregou
      setTimeout(() => this.safeInitialize(), 200);
    } else {
      // Esperar load event
      window.addEventListener('load', () => {
        setTimeout(() => this.safeInitialize(), 200);
      });
    }
  }

  /**
   * Inicialização segura com tratamento de erros
   */
  async safeInitialize() {
    try {
      await this.initialize();
    } catch (error) {
      // Não deixar erro quebrar o resto da página
    }
  }

  /**
   * Inicializa o sistema
   */
  async initialize() {
    if (this.isInitializing || !this.config.enabled) {
      return;
    }

    // Prevenir múltiplas inicializações do mesmo elemento
    if (this.hasAttribute('data-initialized')) {
      return;
    }

    this.isInitializing = true;

    try {
      // Validar que o elemento existe no DOM
      if (!this.isConnected) {
        return;
      }

      // Limpar atributos de carrinho antigos que podem estar causando conflitos
      await this.cleanOldCartAttributes();

      // Buscar elementos DOM com nova estrutura
      this.progressWrapper = this.querySelector('[data-gift-progress-wrapper]');
      this.progressBar = this.querySelector('[data-gift-progress]');
      this.progressFill = this.querySelector('[data-gift-progress-meter]');
      this.progressText = this.querySelector('.progress-meter .text');
      this.progressMessage = this.querySelector('[data-gift-message]');
      this.giftProducts = this.querySelector('[data-gift-products]');
      this.giftsContainer = this.querySelector('[data-gifts-list]');

      // Buscar carrinho atual
      await this.fetchCart();

      // Verificar níveis desbloqueados
      this.checkAllLevels();

      // Aplicar cores personalizadas
      this.applyCustomColors();

      // Atualizar UI
      this.updateUI();

      // Anexar event listeners globais (singleton)
      this.attachEventListeners();

      // Marcar como inicializado
      this.setAttribute('data-initialized', 'true');

    } catch (error) {
      // Erro silencioso
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Aplica cores personalizadas via CSS variables
   */
  applyCustomColors() {
    if (!this.config || !this.config.colors) {
      return;
    }

    const colors = this.config.colors;
    
    // Aplicar no elemento principal (gift-system-container)
    if (this.style) {
      this.style.setProperty('--gift-progress-color-30', colors.progress30 || '#a78bfa');
      this.style.setProperty('--gift-progress-color-60', colors.progress60 || '#8b5cf6');
      this.style.setProperty('--gift-progress-color-100', colors.progress100 || '#7c3aed');
      this.style.setProperty('--gift-progress-color-unlocked', colors.progressUnlocked || '#7c3aed');
      this.style.setProperty('--gift-progress-bg-color', colors.progressBg || '#f0f0f0');
      this.style.setProperty('--gift-progress-text-color', colors.progressText || '#ffffff');
      this.style.setProperty('--gift-message-color-default', colors.messageDefault || '#666666');
      this.style.setProperty('--gift-message-color-30', colors.message30 || '#a78bfa');
      this.style.setProperty('--gift-message-color-60', colors.message60 || '#8b5cf6');
      this.style.setProperty('--gift-message-color-100', colors.message100 || '#7c3aed');
      this.style.setProperty('--gift-message-color-unlocked', colors.messageUnlocked || '#7c3aed');
    }

    // Aplicar também no wrapper da barra de progresso (haloCalculatorGift)
    if (this.progressWrapper && this.progressWrapper.style) {
      this.progressWrapper.style.setProperty('--gift-progress-color-30', colors.progress30 || '#a78bfa');
      this.progressWrapper.style.setProperty('--gift-progress-color-60', colors.progress60 || '#8b5cf6');
      this.progressWrapper.style.setProperty('--gift-progress-color-100', colors.progress100 || '#7c3aed');
      this.progressWrapper.style.setProperty('--gift-progress-color-unlocked', colors.progressUnlocked || '#7c3aed');
      this.progressWrapper.style.setProperty('--gift-progress-bg-color', colors.progressBg || '#f0f0f0');
      this.progressWrapper.style.setProperty('--gift-progress-text-color', colors.progressText || '#ffffff');
      this.progressWrapper.style.setProperty('--gift-message-color-default', colors.messageDefault || '#666666');
      this.progressWrapper.style.setProperty('--gift-message-color-30', colors.message30 || '#a78bfa');
      this.progressWrapper.style.setProperty('--gift-message-color-60', colors.message60 || '#8b5cf6');
      this.progressWrapper.style.setProperty('--gift-message-color-100', colors.message100 || '#7c3aed');
      this.progressWrapper.style.setProperty('--gift-message-color-unlocked', colors.messageUnlocked || '#7c3aed');
    }
  }

  /**
   * Anexa event listeners (singleton - apenas uma vez globalmente)
   */
  attachEventListeners() {
    // Verificar se já foi anexado globalmente
    if (window.giftSystemListenersAttached) {
      return;
    }


    try {
      // Listener para mudanças no carrinho - usar o evento correto do tema
      document.addEventListener('cart-update', (e) => {
        // Ignorar eventos disparados pelo próprio gift system
        if (e.detail && e.detail.source === 'gift-system') {
          return;
        }
        // Atualizar TODOS os gift-system-manager na página
        const managers = document.querySelectorAll('gift-system-manager');
        managers.forEach(manager => {
          // Chamar direto sem debounce quando quick cart está aberto
          if (document.body.classList.contains('cart-sidebar-show')) {
            if (manager.handleCartUpdate) {
              manager.handleCartUpdate();
            }
          } else {
            // Com debounce para outras situações
            if (manager.debouncedCartUpdate) {
              manager.debouncedCartUpdate();
            }
          }
        });
      });

      // Listener para free-shipping component (já existe no tema)
      const freeShipping = document.querySelector('free-shipping-component');
      if (freeShipping) {
        freeShipping.addEventListener('change', () => {
          document.querySelectorAll('gift-system-manager').forEach(manager => {
            if (manager.debouncedCartUpdate) {
              manager.debouncedCartUpdate();
            }
          });
        });
      }

      // Marcar como anexado
      window.giftSystemListenersAttached = true;
    } catch (error) {
      // Erro silencioso
    }
  }

  /**
   * Debounced cart update para evitar múltiplas chamadas
   */
  debouncedCartUpdate() {
    if (this.cartUpdateTimeout) {
      clearTimeout(this.cartUpdateTimeout);
    }
    // Reduzir delay quando quick cart está aberto (otimização)
    const delay = document.body.classList.contains('cart-sidebar-show') ? 300 : 1000;
    this.cartUpdateTimeout = setTimeout(() => {
      this.handleCartUpdate();
    }, delay);
  }

  /**
   * Busca carrinho via Shopify API
   */
  async fetchCart() {
    return new Promise((resolve, reject) => {
      if (typeof Shopify === 'undefined' || !Shopify.getCart) {
        // Fallback: fetch direto
        fetch('/cart.js')
          .then(res => res.json())
          .then(cart => {
            this.currentCart = cart;
            resolve(cart);
          })
          .catch(reject);
      } else {
        Shopify.getCart((cart) => {
          this.currentCart = cart;
          resolve(cart);
        });
      }
    });
  }

  /**
   * Handle cart update
   */
  async handleCartUpdate() {
    // Não bloquear - apenas agendar para depois se estiver processando
    if (this.isProcessing) {
      // Reagendar com delay otimizado baseado no contexto
      const delay = document.body.classList.contains('cart-sidebar-show') ? 300 : 1000;
      setTimeout(() => this.handleCartUpdate(), delay);
      return;
    }

    try {
      // Salvar estado anterior do carrinho antes de atualizar
      this.previousCart = this.currentCart ? JSON.parse(JSON.stringify(this.currentCart)) : null;
      
      await this.fetchCart();

      // Detectar remoção manual de brindes
      this.detectManualGiftRemoval();

      // Resetar bloqueios se requisitos mudaram
      this.resetBlockedLevelsIfNeeded();

      await this.checkAllLevels();

      await this.removeInvalidGifts();
      this.updateUI();
    } catch (error) {
      // Erro silencioso
    }
  }

  /**
   * Calcula total do carrinho excluindo brindes
   */
  getCartTotalExcludingGifts() {
    if (!this.currentCart || !this.currentCart.items) return 0;

    const totalCents = this.currentCart.items.reduce((total, item) => {
      if (item.properties && item.properties._is_gift === 'true') {
        return total;
      }
      return total + (item.original_line_price || item.line_price);
    }, 0);

    return totalCents / 100;
  }

  /**
   * Verifica todos os níveis de brinde
   */
  async checkAllLevels() {
    this.unlockedLevels = [];

    if (!this.currentCart || !this.currentCart.items) {
      return;
    }

    this.levels.forEach((level, index) => {
      if (!level.enabled && index > 0) {
        return; // Nível 1 está sempre enabled
      }

      const eligible = this.checkLevelEligibility(level);

      if (eligible) {
        // Verificar se já não foi adicionado brinde deste nível
        const alreadyHasGift = this.checkIfLevelGiftAdded(level.levelNumber);

        this.unlockedLevels.push({
          ...level,
          alreadyHasGift
        });
      }
    });

    // Processar brindes desbloqueados (adicionar ou exibir escolha)
    await this.processUnlockedGifts();
  }

  /**
   * Verifica se um nível específico foi desbloqueado
   */
  checkLevelEligibility(level) {
    if (!this.currentCart) {
      return false;
    }

    // Calcular total do carrinho EXCLUINDO brindes
    const cartTotalCents = this.currentCart.items.reduce((total, item) => {
      // Ignorar itens que são brindes
      if (item.properties && item.properties._is_gift === 'true') {
        return total;
      }
      return total + (item.original_line_price || item.line_price);
    }, 0);

    const cartTotal = cartTotalCents / 100; // Converter de centavos para reais
    const minValue = level.minValue;

    // Verificar valor mínimo
    if (cartTotal < minValue) {
      return false;
    }

    // Verificar produtos gatilho (se configurado)
    if (level.triggerProducts && level.triggerProducts.length > 0) {

      const hasTriggerProduct = this.currentCart.items.some(item =>
        level.triggerProducts.some(triggerProduct => {
          // triggerProduct pode ser string (handle/id) ou objeto { handle: "..." }
          const productIdentifier = typeof triggerProduct === 'object' && triggerProduct !== null
            ? triggerProduct.handle
            : triggerProduct;

          // Normalizar: remover GID se presente
          const cleanIdentifier = productIdentifier && productIdentifier.toString().includes('gid://shopify/Product/')
            ? productIdentifier.split('/').pop()
            : productIdentifier;

          // Comparar por ID ou handle
          const matchById = item.product_id == cleanIdentifier ||
                            item.product_id === parseInt(cleanIdentifier);
          const matchByHandle = item.handle &&
                                (item.handle === cleanIdentifier ||
                                 item.handle === productIdentifier);

          return matchById || matchByHandle;
        })
      );

      if (!hasTriggerProduct) {
        return false;
      }

    }

    return true;
  }

  /**
   * Gerencia bloqueios de níveis no localStorage
   */
  getBlockedLevels() {
    try {
      const blocked = localStorage.getItem('gift_system_blocked_levels');
      return blocked ? JSON.parse(blocked) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Adiciona um nível à lista de bloqueados
   */
  blockLevel(levelNumber) {
    const blocked = this.getBlockedLevels();
    if (!blocked.includes(levelNumber)) {
      blocked.push(levelNumber);
      localStorage.setItem('gift_system_blocked_levels', JSON.stringify(blocked));
    }
  }

  /**
   * Remove um nível da lista de bloqueados
   */
  unblockLevel(levelNumber) {
    const blocked = this.getBlockedLevels();
    const index = blocked.indexOf(levelNumber);
    if (index > -1) {
      blocked.splice(index, 1);
      localStorage.setItem('gift_system_blocked_levels', JSON.stringify(blocked));
    }
  }

  /**
   * Verifica se um nível está bloqueado
   */
  isLevelBlocked(levelNumber) {
    const blocked = this.getBlockedLevels();
    return blocked.includes(levelNumber);
  }

  /**
   * Limpa todos os bloqueios
   */
  clearAllBlockedLevels() {
    localStorage.removeItem('gift_system_blocked_levels');
  }

  /**
   * Detecta remoção manual de brindes comparando carrinho anterior e atual
   */
  detectManualGiftRemoval() {
    if (!this.previousCart || !this.currentCart) {
      return;
    }

    // Buscar brindes que estavam no carrinho anterior
    const previousGifts = (this.previousCart.items || []).filter(item =>
      item.properties && item.properties._is_gift === 'true'
    );

    // Buscar brindes que estão no carrinho atual
    const currentGifts = (this.currentCart.items || []).filter(item =>
      item.properties && item.properties._is_gift === 'true'
    );

    // Verificar quais brindes foram removidos
    for (const previousGift of previousGifts) {
      const giftStillExists = currentGifts.some(currentGift =>
        currentGift.key === previousGift.key ||
        (currentGift.variant_id === previousGift.variant_id &&
         currentGift.properties._gift_level === previousGift.properties._gift_level)
      );

      if (!giftStillExists) {
        // Brinde foi removido - verificar se foi remoção manual ou automática
        const giftLevel = parseInt(previousGift.properties._gift_level);
        const level = this.levels[giftLevel - 1];
        
        if (!level) continue;

        // Verificar se o nível ainda está elegível com o carrinho atual
        // Se ainda está elegível mas o brinde foi removido, foi remoção manual
        const isStillEligible = this.checkLevelEligibility(level);

        if (isStillEligible) {
          // Nível ainda está elegível mas brinde foi removido = remoção manual
          this.blockLevel(giftLevel);
        }
        // Se não está mais elegível, foi remoção automática (removeInvalidGifts) - não bloquear
      }
    }
  }

  /**
   * Reseta bloqueios quando requisitos do nível mudam
   */
  resetBlockedLevelsIfNeeded() {
    if (!this.currentCart) {
      return;
    }

    const blockedLevels = this.getBlockedLevels();
    if (blockedLevels.length === 0) {
      return;
    }

    // Para cada nível bloqueado, verificar se os requisitos mudaram
    for (const levelNumber of blockedLevels) {
      const level = this.levels[levelNumber - 1];
      if (!level) continue;

      // Verificar se o nível ainda está elegível
      const isEligible = this.checkLevelEligibility(level);

      // Se não está mais elegível, desbloquear (requisitos mudaram)
      if (!isEligible) {
        this.unblockLevel(levelNumber);
      } else {
        // Verificar se havia um brinde anteriormente e ainda existe
        // Se não existe mais e os requisitos continuam os mesmos, manter bloqueado
        const hasGift = this.currentCart.items.some(item =>
          item.properties &&
          item.properties._is_gift === 'true' &&
          item.properties._gift_level == levelNumber
        );

        // Se não tem brinde e requisitos continuam atendidos, manter bloqueado
        // (foi removido manualmente e ainda pode ser readicionado automaticamente)
        // Mas vamos comparar com o carrinho anterior para ver se requisitos mudaram
        
        if (this.previousCart) {
          const previousTotal = this.getCartTotalExcludingGiftsFromCart(this.previousCart);
          const currentTotal = this.getCartTotalExcludingGifts();
          
          // Se o valor total mudou significativamente (mais de 1% de diferença), pode resetar
          const totalChanged = Math.abs(previousTotal - currentTotal) > (level.minValue * 0.01);
          
          // Verificar se produtos gatilho mudaram
          const triggerProductsChanged = this.checkTriggerProductsChanged(level);
          
          if (totalChanged || triggerProductsChanged) {
            // Requisitos mudaram de alguma forma, desbloquear
            this.unblockLevel(levelNumber);
          }
        }
      }
    }
  }

  /**
   * Calcula total do carrinho excluindo brindes (versão com carrinho como parâmetro)
   */
  getCartTotalExcludingGiftsFromCart(cart) {
    if (!cart || !cart.items) return 0;

    const totalCents = cart.items.reduce((total, item) => {
      if (item.properties && item.properties._is_gift === 'true') {
        return total;
      }
      return total + (item.original_line_price || item.line_price);
    }, 0);

    return totalCents / 100;
  }

  /**
   * Verifica se produtos gatilho mudaram entre carrinho anterior e atual
   */
  checkTriggerProductsChanged(level) {
    if (!level.triggerProducts || level.triggerProducts.length === 0) {
      return false; // Sem produtos gatilho, não há mudança
    }

    if (!this.previousCart || !this.currentCart) {
      return false;
    }

    // Verificar se tinha produto gatilho antes
    const hadTriggerBefore = (this.previousCart.items || []).some(item => {
      if (item.properties && item.properties._is_gift === 'true') {
        return false;
      }
      return level.triggerProducts.some(triggerProduct => {
        const productIdentifier = typeof triggerProduct === 'object' && triggerProduct !== null
          ? triggerProduct.handle
          : triggerProduct;
        const cleanIdentifier = productIdentifier && productIdentifier.toString().includes('gid://shopify/Product/')
          ? productIdentifier.split('/').pop()
          : productIdentifier;
        return item.product_id == cleanIdentifier ||
               item.product_id === parseInt(cleanIdentifier) ||
               (item.handle && item.handle === cleanIdentifier);
      });
    });

    // Verificar se tem produto gatilho agora
    const hasTriggerNow = (this.currentCart.items || []).some(item => {
      if (item.properties && item.properties._is_gift === 'true') {
        return false;
      }
      return level.triggerProducts.some(triggerProduct => {
        const productIdentifier = typeof triggerProduct === 'object' && triggerProduct !== null
          ? triggerProduct.handle
          : triggerProduct;
        const cleanIdentifier = productIdentifier && productIdentifier.toString().includes('gid://shopify/Product/')
          ? productIdentifier.split('/').pop()
          : productIdentifier;
        return item.product_id == cleanIdentifier ||
               item.product_id === parseInt(cleanIdentifier) ||
               (item.handle && item.handle === cleanIdentifier);
      });
    });

    // Se mudou (tinha e não tem mais, ou não tinha e tem agora)
    return hadTriggerBefore !== hasTriggerNow;
  }

  /**
   * Processa brindes desbloqueados
   */
  async processUnlockedGifts() {

    if (this.isProcessing) {
      return;
    }


    let hasGiftsAlready = false;
    let needsToAddGift = false;

    for (const level of this.unlockedLevels) {

      if (level.alreadyHasGift) {
        hasGiftsAlready = true;
        continue; // Já tem brinde deste nível
      }

      // Verificar se o nível está bloqueado (brinde foi removido manualmente)
      if (this.isLevelBlocked(level.levelNumber)) {
        continue; // Não adicionar brinde se nível está bloqueado
      }

      const availableGifts = level.availableGifts || [];

      if (availableGifts.length > 0) {
      }

      if (!availableGifts || availableGifts.length === 0) {
        continue;
      }

      needsToAddGift = true;

      // SEMPRE adiciona primeiro brinde automaticamente (sem interface de seleção)
      if (availableGifts && availableGifts.length > 0) {
        await this.addGiftAutomatically(availableGifts[0], level);
      }
    }

    // Se já tinha brindes mas não adicionou novos, forçar refresh para sincronizar UI
    if (hasGiftsAlready && !needsToAddGift && document.body.classList.contains('cart-sidebar-show')) {

      // Refresh com delay de 1 segundo para dar tempo do tema processar
      setTimeout(() => {

        // Recarregar HTML do quick cart via AJAX (igual o tema faz)
        if (typeof $ !== 'undefined' && window.routes && window.routes.root !== undefined) {
          const $cartDropdown = $('#halo-cart-sidebar .halo-sidebar-wrapper .previewCart-wrapper');

          $.ajax({
            type: 'GET',
            url: window.routes.root + '/cart?view=ajax_side_cart',
            cache: false,
            success: function (data) {
              const response = $(data);
              $cartDropdown.html(response);
            },
            error: function () {
            }
          });
        } else {
        }
      }, 1000);
    }
  }

  /**
   * Adiciona brinde automaticamente (quando há apenas 1 opção)
   */
  async addGiftAutomatically(productHandleOrObj, level) {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      // Extrair handle do produto (pode vir como string ou objeto)
      const productHandle = typeof productHandleOrObj === 'string'
        ? productHandleOrObj
        : (productHandleOrObj?.handle || productHandleOrObj);


      // Buscar dados do produto
      const product = await this.fetchProductByHandle(productHandle);

      if (!product || !product.variants || product.variants.length === 0) {
        throw new Error('Produto de brinde não encontrado');
      }

      const variant = product.variants[0];

      // Verificação: Este produto já está no carrinho COMO BRINDE?
      const giftAlreadyInCart = this.currentCart.items.some(item =>
        (item.handle === product.handle || item.variant_id === variant.id) &&
        item.properties && item.properties._is_gift === 'true'
      );

      if (giftAlreadyInCart) {
        return; // SAIR SEM ADICIONAR BRINDE
      }

      // Verificar se produto normal existe (apenas para log informativo)
      const regularProductInCart = this.currentCart.items.some(item =>
        (item.handle === product.handle || item.variant_id === variant.id) &&
        (!item.properties || item.properties._is_gift !== 'true')
      );

      if (regularProductInCart) {
      }

      // Verificar estoque
      if (!variant.available) {
        this.showError(window.giftSystemTranslations?.errors?.out_of_stock || 'Brinde indisponível no momento');
        return;
      }

      // Adicionar ao carrinho com propriedades especiais
      const properties = {
        '_is_gift': 'true',
        '_gift_level': level.levelNumber.toString(),
        '_gift_discount_percent': level.discountPercent.toString(),
        '_original_price': variant.price.toString()
      };

      // Otimização: addToCart já atualiza o carrinho e dispara eventos
      // Passar o carrinho atualizado para refreshCart para evitar busca redundante
      await this.addToCart(variant.id, 1, properties);
      
      // REMOVIDO: applyGiftDiscount causava conflito ao aplicar desconto em todos os produtos com mesmo variant_id
      // O desconto já é aplicado via properties do line item (_gift_discount_percent)

      // Atualizar tracking
      this.updateAnalytics('gift_auto_added', {
        level: level.levelNumber,
        product_id: product.id,
        variant_id: variant.id
      });

      // Otimização: refreshCart recebe carrinho já atualizado (currentCart foi atualizado em addToCart)
      // Apenas atualizar UI se necessário, sem buscar carrinho novamente
      // Se addToCart já disparou evento cart-update, o tema já vai atualizar automaticamente
      // Mas ainda precisamos atualizar a UI do quick cart se estiver aberto
      if (document.body.classList.contains('cart-sidebar-show')) {
        // Apenas atualizar quick cart, sem buscar carrinho novamente
        await this.refreshCart(this.currentCart);
      }

      // Mostrar popup de sucesso (se configurado)
      if (this.config.showPopup) {
        this.showSuccessPopup(product, 'single');
      }

    } catch (error) {
      this.showError(window.giftSystemTranslations?.errors?.failed_to_add || 'Erro ao adicionar brinde');
    } finally {
      this.isProcessing = false;
    }
  }

  async addToCart(variantId, quantity, properties) {
    const formData = {
      items: [{
        id: variantId,
        quantity: quantity,
        properties: properties
      }]
    };

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Erro ao adicionar ao carrinho');
    }

    const result = await response.json();

    // Otimização: Usar Shopify.getCart para atualizar estado e disparar evento
    // Elimina a necessidade de fetchCart() separado
    return new Promise((resolve, reject) => {
      if (typeof Shopify !== 'undefined' && Shopify.getCart) {
        Shopify.getCart((cart) => {
          // Atualizar carrinho internamente usando resultado de Shopify.getCart
          this.currentCart = cart;
          
          // Disparar evento de atualização para que o tema atualize a UI
          document.dispatchEvent(new CustomEvent('cart-update', {
            detail: cart
          }));
          
          resolve(result);
        });
      } else {
        // Fallback: buscar carrinho se Shopify.getCart não estiver disponível
        this.fetchCart().then(() => {
          resolve(result);
        }).catch(reject);
      }
    });
  }

  /**
   * Atualiza o carrinho e dispara eventos para UI
   * @param {Object} cart - Carrinho opcional (se já carregado, evita requisição redundante)
   */
  async refreshCart(cart = null) {
    try {
      // Otimização: Se carrinho já foi passado, usar diretamente ao invés de buscar novamente
      if (cart) {
        this.currentCart = cart;
      } else {
        // Atualizar carrinho internamente apenas se não foi fornecido
        await this.fetchCart();
      }

      // Se está na página do carrinho, recarregar
      if (window.location.pathname === '/cart') {
        window.location.reload();
        return;
      }

      // Se quick cart está aberto, recarregar via AJAX
      if (document.body.classList.contains('cart-sidebar-show')) {
        if (typeof $ !== 'undefined' && window.routes && window.routes.root !== undefined) {
          const $cartDropdown = $('#halo-cart-sidebar .halo-sidebar-wrapper .previewCart-wrapper');

          $.ajax({
            type: 'GET',
            url: window.routes.root + '/cart?view=ajax_side_cart',
            cache: false,
            success: function (data) {
              const response = $(data);
              $cartDropdown.html(response);
            }
          });
        }
      }

    } catch (error) {
    }
  }

  /**
   * Limpa atributos de carrinho antigos que podem estar causando conflitos
   */
  async cleanOldCartAttributes() {
    try {
      // Buscar o carrinho atual
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Verificar se existem atributos antigos do tipo _gift_{variant_id}_discount
      if (cart.attributes) {
        const attributesToRemove = {};
        let hasOldAttributes = false;

        // Procurar por atributos antigos de desconto
        for (const key in cart.attributes) {
          if (key.startsWith('_gift_') && key.endsWith('_discount')) {
            attributesToRemove[key] = ''; // String vazia remove o atributo
            hasOldAttributes = true;
          }
        }

        // Se encontrou atributos antigos, removê-los
        if (hasOldAttributes) {

          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              attributes: attributesToRemove
            })
          });

          if (response.ok) {
          }
        }
      }
    } catch (error) {
      // Erro silencioso
    }
  }

  /**
   * Aplica desconto no brinde via cart attributes
   */
  async applyGiftDiscount(variantId, discountPercent) {
    const attributeKey = `_gift_${variantId}_discount`;

    const updates = {
      attributes: {
        [attributeKey]: discountPercent.toString()
      }
    };


    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        // Erro silencioso
      }


      // Se desconto for 100%, tentar aplicar código de desconto automático
      if (discountPercent === 100) {
        const discountCode = `GIFT-${variantId}`;
        localStorage.setItem('storedDiscount', discountCode);
      }

    } catch (error) {
      // Erro silencioso
    }
  }

  /**
   * Busca produto por handle (com cache para otimização)
   */
  async fetchProductByHandle(handle) {
    // Remover 'gid://shopify/Product/' se vier do product_list
    const cleanHandle = handle.toString().replace('gid://shopify/Product/', '');

    // Otimização: Verificar cache primeiro
    if (this.productCache.has(cleanHandle)) {
      return this.productCache.get(cleanHandle);
    }

    let product = null;

    // Se for ID numérico, buscar por ID
    if (!isNaN(cleanHandle)) {
      // Converter ID para handle via metafield ou busca
      // Por enquanto, tentar buscar direto
      const response = await fetch(`/products/${cleanHandle}.js`);
      if (response.ok) {
        product = await response.json();
      }
    }

    // Se não encontrou por ID ou não é ID numérico, buscar por handle
    if (!product) {
      const response = await fetch(`/products/${cleanHandle}.js`);

      if (!response.ok) {
        throw new Error(`Produto não encontrado: ${cleanHandle}`);
      }

      product = await response.json();
    }

    // Armazenar no cache para próximas chamadas
    if (product) {
      this.productCache.set(cleanHandle, product);
    }

    return product;
  }

  /**
   * Verifica se já tem brinde de determinado nível no carrinho
   */
  checkIfLevelGiftAdded(levelNumber) {
    if (!this.currentCart || !this.currentCart.items) {
      return false;
    }

    return this.currentCart.items.some(item =>
      item.properties &&
      item.properties._is_gift === 'true' &&
      item.properties._gift_level == levelNumber
    );
  }

  /**
   * Remove brindes inválidos (quando regras não são mais atendidas)
   */
  async removeInvalidGifts() {
    if (!this.currentCart || !this.currentCart.items) {
      return;
    }

    const giftsInCart = this.currentCart.items.filter(item =>
      item.properties && item.properties._is_gift === 'true'
    );

    let giftRemoved = false;

    for (const gift of giftsInCart) {
      const giftLevel = parseInt(gift.properties._gift_level);
      const levelStillUnlocked = this.unlockedLevels.some(
        level => level.levelNumber === giftLevel
      );

      if (!levelStillUnlocked) {
        // Remover brinde
        try {

          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: gift.key,
              quantity: 0
            })
          });

          giftRemoved = true;

          // Analytics
          this.updateAnalytics('gift_removed', {
            level: giftLevel,
            reason: 'level_no_longer_unlocked'
          });

        } catch (error) {
          // Erro silencioso
        }
      }
    }

    // Se removeu algum brinde, atualizar o carrinho na UI
    if (giftRemoved) {
      await this.refreshCart();
    }
  }

  /**
   * Atualiza toda a UI
   */
  updateUI() {
    // Reaplicar cores personalizadas (caso tenham mudado)
    this.applyCustomColors();
    this.updateProgressBar();
  }

  /**
   * Atualiza barra de progresso
   */
  updateProgressBar() {
    if (!this.progressFill || !this.progressMessage) {
      return;
    }

    if (!this.currentCart) {
      this.progressFill.style.width = '0%';
      if (this.progressText) this.progressText.textContent = '0%';
      return;
    }

    // Calcular total do carrinho EXCLUINDO brindes
    const cartTotalCents = this.currentCart.items.reduce((total, item) => {
      // Ignorar itens que são brindes
      if (item.properties && item.properties._is_gift === 'true') {
        return total;
      }
      return total + (item.original_line_price || item.line_price);
    }, 0);

    const cartTotal = cartTotalCents / 100;

    // Encontrar próximo nível a ser desbloqueado
    const nextLevel = this.levels.find(level => {
      if (!level.enabled && level.levelNumber > 1) {
        return false;
      }
      return cartTotal < level.minValue;
    });

    if (!nextLevel) {
      // Verificar se o último nível tem todos os requisitos atendidos
      const lastLevel = this.levels.filter(l => l.enabled || l.levelNumber === 1)
                                   .sort((a, b) => b.minValue - a.minValue)[0];

      if (lastLevel && cartTotal >= lastLevel.minValue) {
        // Valor atingido, verificar trigger products
        const isFullyUnlocked = this.checkLevelEligibility(lastLevel);

        if (isFullyUnlocked) {
          // ✅ Todos os requisitos atendidos
          this.progressFill.style.width = '100%';
          if (this.progressText) this.progressText.textContent = '100%';
          this.progressMessage.textContent = this.config.messageUnlockedSingle || window.giftSystemTranslations?.progress?.complete || '🎁 Parabéns! Você ganhou um brinde!';
          // Adicionar classes de estado
          this.setProgressClasses('progress-free');
        } else {
          // ⚠️ Valor OK mas falta trigger product
          this.progressFill.style.width = '100%';
          if (this.progressText) this.progressText.textContent = '100%';
          this.progressMessage.textContent = this.config.messageTriggerMissing || 'Adicione um dos produtos especiais para desbloquear o brinde!';
          // Adicionar classes de estado
          this.setProgressClasses('progress-100');
        }
      } else {
        // Todos os níveis desbloqueados
        this.progressFill.style.width = '100%';
        if (this.progressText) this.progressText.textContent = '100%';
        this.progressMessage.textContent = this.config.messageUnlockedSingle || window.giftSystemTranslations?.progress?.complete || 'Todos os brindes desbloqueados!';
        this.setProgressClasses('progress-free');
      }
      return;
    }

    const progress = Math.min((cartTotal / nextLevel.minValue) * 100, 100);
    const remaining = nextLevel.minValue - cartTotal;

    this.progressFill.style.width = `${progress}%`;
    if (this.progressText) this.progressText.textContent = `${Math.round(progress)}%`;

    // Verificar se progresso está em 100% mas precisa de trigger product
    if (progress >= 100 && nextLevel.triggerProducts && nextLevel.triggerProducts.length > 0) {
      // Verificar se trigger product está presente
      const hasRequiredProduct = this.checkLevelEligibility(nextLevel);

      if (!hasRequiredProduct) {
        // Valor atingido mas falta trigger product
        this.progressMessage.textContent = this.config.messageTriggerMissing || 'Adicione um dos produtos especiais para desbloquear o brinde!';
        this.setProgressClasses('progress-100');
      } else {
        // Tudo OK - usar mensagem padrão
        const message = (this.config.messageProgress || 'Falta apenas {valor} para ganhar um brinde!')
          .replace('{valor}', this.formatCurrency(remaining))
          .replace('{nivel}', nextLevel.levelNumber);
        this.progressMessage.textContent = message;
        this.setProgressClasses('progress-100');
      }
    } else {
      // Formatar mensagem normal
      const message = (this.config.messageProgress || 'Falta apenas {valor} para ganhar um brinde!')
        .replace('{valor}', this.formatCurrency(remaining))
        .replace('{nivel}', nextLevel.levelNumber);
      this.progressMessage.textContent = message;

      // Determinar classe baseada no progresso
      let progressClass = 'progress-30';
      if (progress >= 60) progressClass = 'progress-60';
      if (progress >= 100) progressClass = 'progress-100';
      this.setProgressClasses(progressClass);
    }
  }

  /**
   * Define as classes de estado da barra de progresso
   */
  setProgressClasses(className) {
    // Remover classes antigas
    const classes = ['progress-free', 'progress-30', 'progress-60', 'progress-100'];

    if (this.progressBar) {
      classes.forEach(cls => this.progressBar.classList.remove(cls));
      this.progressBar.classList.add(className);
    }

    if (this.progressMessage) {
      classes.forEach(cls => this.progressMessage.classList.remove(cls));
      this.progressMessage.classList.add(className);
    }
  }

  /**
   * Mostra popup de sucesso
   */
  showSuccessPopup(product, type) {
    return; // Popup desabilitado, agora usamos a barra de progresso

    const popup = document.querySelector('[data-gift-success-popup]');

    if (!popup) {
      return;
    }


    const message = type === 'single'
      ? (this.config.messageUnlockedSingle || '🎁 Parabéns! Seu brinde foi adicionado!')
      : (this.config.messageUnlockedMultiple || '🎉 Brinde selecionado!');

    const messageEl = popup.querySelector('[data-popup-message]');
    const titleEl = popup.querySelector('[data-popup-product-title]');
    const imageEl = popup.querySelector('[data-popup-product-image]');


    if (messageEl) {
      messageEl.textContent = message;
    }

    if (titleEl) {
      titleEl.textContent = product.title;
    } else {
      // Se titleEl não existe, criar ele
      const productContainer = popup.querySelector('.gift-success-product');
      if (productContainer) {
        const newTitleEl = document.createElement('h3');
        newTitleEl.className = 'gift-success-product-title';
        newTitleEl.setAttribute('data-popup-product-title', '');
        newTitleEl.textContent = product.title;
        productContainer.appendChild(newTitleEl);
      }
    }

    if (imageEl) {
      imageEl.src = product.featured_image || (product.images && product.images[0]) || '';
      imageEl.alt = product.title;
      // Garantir que a imagem respeite o CSS
      imageEl.style.cssText = '';
    }


    // Forçar exibição do popup (ModalDialog pode não estar registrado)
    popup.setAttribute('open', '');

    // Forçar display inline também via style para garantir visibilidade
    popup.style.display = 'flex';
    popup.style.alignItems = 'center';
    popup.style.justifyContent = 'center';
    popup.style.position = 'fixed';
    popup.style.top = '0';
    popup.style.left = '0';
    popup.style.width = '100%';
    popup.style.height = '100%';
    popup.style.zIndex = '99999';

    // Forçar background no overlay e conteúdo
    const overlay = popup.querySelector('.gift-popup-overlay');
    const content = popup.querySelector('.gift-popup-content');
    const innerContent = popup.querySelector('.gift-success-content');

    if (overlay) {
      overlay.style.display = 'block';
    }

    if (content) {
      // Forçar apenas display e z-index, deixar CSS cuidar do resto
      content.style.cssText = 'display: block !important; position: relative !important; z-index: 100000 !important;';
    }

    if (innerContent) {
      innerContent.style.display = 'block';
      innerContent.style.visibility = 'visible';
    }

    // Adicionar overflow-hidden no body para bloquear scroll
    document.body.classList.add('overflow-hidden');


    // Confete (se configurado)
    if (this.config.popupStyle === 'celebration') {
      this.triggerConfetti();
    }

    // Fechar automaticamente após 5s
    setTimeout(() => {
      popup.removeAttribute('open');
      popup.style.display = 'none';
      document.body.classList.remove('overflow-hidden');
    }, 5000);
  }

  /**
   * Trigger confete animation
   */
  triggerConfetti() {
    // Implementação simples de confete
    // Se biblioteca canvas-confetti estiver disponível
    if (typeof confetti === 'function') {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }

  /**
   * Mostra mensagem de erro
   */
  showError(message) {
    // Mostrar em um toast/alert simples
    // Pode ser melhorado com um componente de notificação
    if (window.alert) {
      alert(message);
    }
  }

  /**
   * Atualiza analytics/tracking
   */
  updateAnalytics(eventType, data) {
    if (!this.config.enableAnalytics) {
      return;
    }

    const tracking = {
      timestamp: new Date().toISOString(),
      event: eventType,
      ...data
    };

    // Salvar em cart attributes
    const currentTracking = this.currentCart.attributes && this.currentCart.attributes._gift_tracking
      ? JSON.parse(this.currentCart.attributes._gift_tracking)
      : [];

    currentTracking.push(tracking);

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          _gift_tracking: JSON.stringify(currentTracking)
        }
      })
    }).catch(error => {
      // Erro silencioso
    });

    // Google Analytics (se disponível)
    if (typeof gtag === 'function') {
      gtag('event', eventType, {
        event_category: 'Gift System',
        ...data
      });
    }

    // Facebook Pixel (se disponível)
    if (typeof fbq === 'function') {
      fbq('trackCustom', 'GiftSystem_' + eventType, data);
    }
  }

  /**
   * Formata valor em moeda BRL
   */
  formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }
}

// Registrar Custom Element
customElements.define('gift-system-manager', GiftSystemManager);

// Auto-inicializar após mudanças no carrinho
document.addEventListener('DOMContentLoaded', () => {
  const giftSystem = document.querySelector('gift-system-manager');

  if (giftSystem) {
    // Re-inicializar após mudanças no carrinho
    document.addEventListener('cart:updated', () => {
      if (!giftSystem.isInitializing && !giftSystem.isProcessing) {
        giftSystem.handleCartUpdate();
      }
    });
  }
});

